// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/MentorIA/MentorIA.tsx
//  Interface de chat com o Mentor IA (Claude) — SSE streaming
// ════════════════════════════════════════════════════════════════════════════

import { useState, useRef, useEffect, useCallback } from 'react';
import { Bot, Plus, Send, Trash2, Sparkles, User, Loader2, StopCircle, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/hooks/useAuth';
import { mentorService } from '@/features/mentor/services/mentorService';
import {
  useConversas, useCriarConversa, useArquivarConversa,
} from '@/features/mentor/hooks/useMentor';
import type { MensagemIA, ConversaIA } from '@/features/mentor/types';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';

const normalizar = (s: string) =>
  s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

// ── Renderizador de Markdown simples ─────────────────────────────────────

function MarkdownText({ text }: { text: string }) {
  // Converte blocos de código, negrito, itálico e listas básicas
  const lines = text.split('\n');
  const result: React.ReactNode[] = [];
  let inCodeBlock = false;
  let codeLines:   string[]       = [];
  let blockLang    = '';

  const flush = (idx: number) => {
    if (codeLines.length > 0) {
      result.push(
        <pre key={`code-${idx}`} className="my-2 rounded-md bg-muted/80 p-3 text-xs font-mono overflow-x-auto whitespace-pre">
          <code>{codeLines.join('\n')}</code>
        </pre>
      );
      codeLines = [];
    }
  };

  lines.forEach((line, idx) => {
    if (line.startsWith('```')) {
      if (!inCodeBlock) {
        inCodeBlock = true;
        blockLang   = line.slice(3).trim();
      } else {
        inCodeBlock = false;
        flush(idx);
        blockLang   = '';
      }
      return;
    }

    if (inCodeBlock) { codeLines.push(line); return; }

    // Inline formatting
    const formatted = formatInline(line);

    if (line.startsWith('### ')) {
      result.push(<h3 key={idx} className="mt-3 mb-1 font-semibold text-base">{formatInline(line.slice(4))}</h3>);
    } else if (line.startsWith('## ')) {
      result.push(<h2 key={idx} className="mt-4 mb-1 font-bold text-lg">{formatInline(line.slice(3))}</h2>);
    } else if (line.startsWith('# ')) {
      result.push(<h1 key={idx} className="mt-4 mb-1 font-bold text-xl">{formatInline(line.slice(2))}</h1>);
    } else if (line.startsWith('- ') || line.startsWith('* ')) {
      result.push(<li key={idx} className="ml-4 list-disc text-sm">{formatInline(line.slice(2))}</li>);
    } else if (/^\d+\.\s/.test(line)) {
      result.push(<li key={idx} className="ml-4 list-decimal text-sm">{formatInline(line.replace(/^\d+\.\s/, ''))}</li>);
    } else if (line === '') {
      result.push(<br key={idx} />);
    } else {
      result.push(<p key={idx} className="text-sm leading-relaxed">{formatted}</p>);
    }
  });

  flush(lines.length);
  return <div className="space-y-0.5">{result}</div>;
}

function formatInline(text: string): React.ReactNode {
  // Negrito **text** e *text*, código `code`
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**'))
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    if (part.startsWith('*') && part.endsWith('*'))
      return <em key={i}>{part.slice(1, -1)}</em>;
    if (part.startsWith('`') && part.endsWith('`'))
      return <code key={i} className="rounded bg-muted px-1 py-0.5 text-xs font-mono">{part.slice(1, -1)}</code>;
    return part;
  });
}

// ── Bolha de mensagem ─────────────────────────────────────────────────────

function Bolha({ msg, streaming }: { msg: MensagemIA; streaming?: boolean }) {
  const isUser = msg.role === 'user';
  return (
    <div className={cn('flex gap-3 px-4 py-3', isUser ? 'justify-end' : 'justify-start')}>
      {!isUser && (
        <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10">
          <Bot className="h-4 w-4 text-primary" />
        </div>
      )}
      <div
        className={cn(
          'max-w-[75%] rounded-2xl px-4 py-2.5 text-sm shadow-sm',
          isUser
            ? 'rounded-br-sm bg-primary text-primary-foreground'
            : 'rounded-bl-sm bg-muted text-foreground'
        )}
      >
        {isUser
          ? <p className="whitespace-pre-wrap leading-relaxed">{msg.conteudo}</p>
          : <MarkdownText text={msg.conteudo} />
        }
        {streaming && (
          <span className="ml-1 inline-block h-3 w-0.5 animate-pulse bg-current" />
        )}
      </div>
      {isUser && (
        <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-secondary">
          <User className="h-4 w-4 text-secondary-foreground" />
        </div>
      )}
    </div>
  );
}

// ── Item de conversa na sidebar ───────────────────────────────────────────

function ConversaItem({
  conversa,
  ativa,
  onSelect,
  onArquivar,
}: {
  conversa: ConversaIA;
  ativa: boolean;
  onSelect: () => void;
  onArquivar: () => void;
}) {
  return (
    <div
      className={cn(
        'group flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors hover:bg-accent',
        ativa && 'bg-accent font-medium'
      )}
      onClick={onSelect}
    >
      <Bot className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      <span className="flex-1 truncate text-foreground">
        {conversa.titulo || 'Nova conversa'}
      </span>
      <Button
        size="icon"
        variant="ghost"
        className="h-5 w-5 shrink-0 opacity-0 group-hover:opacity-100"
        onClick={(e) => { e.stopPropagation(); onArquivar(); }}
        title="Arquivar"
      >
        <Trash2 className="h-3 w-3 text-muted-foreground" />
      </Button>
    </div>
  );
}

// ── Tela principal ────────────────────────────────────────────────────────

const MentorIA = () => {
  const { user } = useAuth();
  const idEmpresa = user?.empresa?.id ?? 0;

  const { data: conversas = [], refetch: refetchConversas } = useConversas(idEmpresa);
  const [buscaConversa, setBuscaConversa] = useState('');
  const termoConversa = normalizar(buscaConversa.trim());
  const conversasFiltradas = termoConversa
    ? conversas.filter((c) => normalizar(c.titulo || 'Nova conversa').includes(termoConversa))
    : conversas;
  const criarConversa   = useCriarConversa(idEmpresa);
  const arquivarMutation = useArquivarConversa(idEmpresa);

  const [conversaAtiva,    setConversaAtiva]    = useState<ConversaIA | null>(null);
  const [mensagens,        setMensagens]        = useState<MensagemIA[]>([]);
  const [streamingTexto,   setStreamingTexto]   = useState('');
  const [isStreaming,      setIsStreaming]       = useState(false);
  const [input,            setInput]            = useState('');
  const [carregandoMsgs,   setCarregandoMsgs]   = useState(false);

  const bottomRef = useRef<HTMLDivElement>(null);
  const abortRef  = useRef<AbortController | null>(null);

  // Rola para o final quando novas mensagens chegam
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [mensagens, streamingTexto]);

  // Carrega mensagens ao selecionar conversa
  const selecionarConversa = useCallback(async (conv: ConversaIA) => {
    setConversaAtiva(conv);
    setStreamingTexto('');
    setCarregandoMsgs(true);
    try {
      const detalhe = await mentorService.buscarConversa(conv.id);
      setMensagens(detalhe.mensagens ?? []);
    } finally {
      setCarregandoMsgs(false);
    }
  }, []);

  // Nova conversa
  const handleNovaConversa = async () => {
    const nova = await criarConversa.mutateAsync({ idEmpresa });
    setConversaAtiva(nova);
    setMensagens([]);
    setStreamingTexto('');
  };

  // Arquivar conversa
  const handleArquivar = async (id: number) => {
    await arquivarMutation.mutateAsync(id);
    if (conversaAtiva?.id === id) {
      setConversaAtiva(null);
      setMensagens([]);
    }
  };

  // Enviar mensagem com streaming
  const handleEnviar = async () => {
    if (!input.trim() || isStreaming) return;

    let convId = conversaAtiva?.id;

    // Cria conversa se não existe
    if (!convId) {
      const nova = await criarConversa.mutateAsync({ idEmpresa });
      setConversaAtiva(nova);
      convId = nova.id;
    }

    const textoEnviado = input.trim();
    setInput('');

    // Adiciona mensagem do usuário localmente
    const msgUser: MensagemIA = {
      id: Date.now(),
      idConversa: convId,
      role: 'user',
      conteudo: textoEnviado,
      tokensUsados: 0,
      dtCriacao: new Date().toISOString(),
    };
    setMensagens((prev) => [...prev, msgUser]);
    setStreamingTexto('');
    setIsStreaming(true);

    const abort = new AbortController();
    abortRef.current = abort;

    let textoAcumulado = '';

    try {
      await mentorService.streamMensagem(
        convId,
        { mensagem: textoEnviado },
        (chunk) => {
          textoAcumulado += chunk;
          setStreamingTexto(textoAcumulado);
        },
        (id) => {
          // Streaming concluído: adiciona mensagem do assistente
          const msgAssistant: MensagemIA = {
            id: Date.now() + 1,
            idConversa: id,
            role: 'assistant',
            conteudo: textoAcumulado,
            tokensUsados: 0,
            dtCriacao: new Date().toISOString(),
          };
          setMensagens((prev) => [...prev, msgAssistant]);
          setStreamingTexto('');
          setIsStreaming(false);
          // Atualiza lista de conversas (título pode ter mudado)
          refetchConversas();
        },
        (err) => {
          setIsStreaming(false);
          setStreamingTexto('');
          const errMsg: MensagemIA = {
            id: Date.now() + 1,
            idConversa: convId!,
            role: 'assistant',
            conteudo: `Erro ao obter resposta: ${err}`,
            tokensUsados: 0,
            dtCriacao: new Date().toISOString(),
          };
          setMensagens((prev) => [...prev, errMsg]);
        },
        abort.signal,
      );
    } catch (e) {
      if ((e as Error).name !== 'AbortError') {
        setIsStreaming(false);
        setStreamingTexto('');
      }
    }
  };

  const handleParar = () => {
    abortRef.current?.abort();
    if (streamingTexto) {
      const msgAssistant: MensagemIA = {
        id: Date.now(),
        idConversa: conversaAtiva?.id ?? 0,
        role: 'assistant',
        conteudo: streamingTexto + ' *(interrompido)*',
        tokensUsados: 0,
        dtCriacao: new Date().toISOString(),
      };
      setMensagens((prev) => [...prev, msgAssistant]);
    }
    setStreamingTexto('');
    setIsStreaming(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleEnviar();
    }
  };

  // Mensagem de streaming como bolha temporária
  const streamingMsg: MensagemIA | null = isStreaming
    ? { id: -1, idConversa: conversaAtiva?.id ?? 0, role: 'assistant', conteudo: streamingTexto, tokensUsados: 0, dtCriacao: '' }
    : null;

  return (
    <div className="flex h-[calc(100vh-6rem)] overflow-hidden rounded-xl border bg-background shadow-sm">

      {/* ── Sidebar: lista de conversas ────────────────────────────────── */}
      <div className="flex w-64 flex-col border-r bg-muted/30">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            <span className="font-semibold text-sm">Mentor IA</span>
          </div>
          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={handleNovaConversa}
            title="Nova conversa" disabled={criarConversa.isPending}>
            <Plus className="h-4 w-4" />
          </Button>
        </div>
        <Separator />
        {conversas.length > 0 && (
          <div className="relative px-2 pt-2">
            <Search className="absolute left-4 top-4 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              className="h-8 pl-7 text-xs"
              placeholder="Buscar conversa..."
              value={buscaConversa}
              onChange={(e) => setBuscaConversa(e.target.value)}
            />
          </div>
        )}
        <ScrollArea className="flex-1 px-2 py-2">
          {conversas.length === 0 ? (
            <p className="px-3 py-4 text-xs text-muted-foreground text-center">
              Nenhuma conversa ainda.<br />Clique em + para começar.
            </p>
          ) : conversasFiltradas.length === 0 ? (
            <p className="px-3 py-4 text-xs text-muted-foreground text-center">
              Nenhuma conversa encontrada.
            </p>
          ) : (
            conversasFiltradas.map((c) => (
              <ConversaItem
                key={c.id}
                conversa={c}
                ativa={conversaAtiva?.id === c.id}
                onSelect={() => selecionarConversa(c)}
                onArquivar={() => handleArquivar(c.id)}
              />
            ))
          )}
        </ScrollArea>
      </div>

      {/* ── Área principal: chat ───────────────────────────────────────── */}
      <div className="flex flex-1 flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center gap-3 border-b px-6 py-3">
          <Bot className="h-5 w-5 text-primary" />
          <div>
            <h1 className="font-semibold text-foreground">
              {conversaAtiva?.titulo || 'Mentor IA — Assistente Udisys'}
            </h1>
            <p className="text-xs text-muted-foreground">
              Powered by Claude · Anthropic
            </p>
          </div>
        </div>

        {/* Mensagens */}
        <ScrollArea className="flex-1">
          {!conversaAtiva ? (
            // Tela de boas-vindas
            <div className="flex h-full flex-col items-center justify-center gap-4 p-8 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10">
                <Sparkles className="h-8 w-8 text-primary" />
              </div>
              <div>
                <h2 className="text-xl font-bold">Olá! Sou o Mentor IA</h2>
                <p className="mt-2 text-sm text-muted-foreground max-w-sm">
                  Estou aqui para ajudar com análises do Udisys Gestor, gerar relatórios,
                  responder dúvidas sobre o sistema e muito mais.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2 max-w-md w-full">
                {[
                  'Qual foi o faturamento do último mês?',
                  'Liste os produtos com estoque abaixo do mínimo',
                  'Quais clientes têm contas em atraso?',
                  'Resuma as movimentações de hoje',
                ].map((sugestao) => (
                  <button
                    key={sugestao}
                    className="rounded-lg border bg-muted/50 px-3 py-2 text-left text-xs text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
                    onClick={() => { setInput(sugestao); }}
                  >
                    {sugestao}
                  </button>
                ))}
              </div>
              <Button onClick={handleNovaConversa} className="gap-2 mt-2">
                <Plus className="h-4 w-4" />Nova Conversa
              </Button>
            </div>
          ) : carregandoMsgs ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="py-2">
              {mensagens.length === 0 && !isStreaming && (
                <p className="py-8 text-center text-sm text-muted-foreground">
                  Envie uma mensagem para começar.
                </p>
              )}
              {mensagens.map((msg) => (
                <Bolha key={msg.id} msg={msg} />
              ))}
              {streamingMsg && (
                <Bolha msg={streamingMsg} streaming />
              )}
              {isStreaming && !streamingTexto && (
                <div className="flex gap-3 px-4 py-3">
                  <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10">
                    <Bot className="h-4 w-4 text-primary" />
                  </div>
                  <div className="rounded-2xl rounded-bl-sm bg-muted px-4 py-2.5">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  </div>
                </div>
              )}
              <div ref={bottomRef} />
            </div>
          )}
        </ScrollArea>

        {/* Input */}
        <div className="border-t bg-background px-4 py-3">
          <div className="flex items-end gap-2 rounded-xl border bg-muted/30 px-3 py-2">
            <Textarea
              className="min-h-[36px] max-h-[160px] flex-1 resize-none border-0 bg-transparent p-0 text-sm shadow-none focus-visible:ring-0"
              placeholder="Pergunte algo ao Mentor IA... (Enter para enviar, Shift+Enter para nova linha)"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isStreaming}
              rows={1}
            />
            {isStreaming ? (
              <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={handleParar}>
                <StopCircle className="h-4 w-4 text-destructive" />
              </Button>
            ) : (
              <Button
                size="icon"
                className="h-8 w-8 shrink-0"
                onClick={handleEnviar}
                disabled={!input.trim()}
              >
                <Send className="h-4 w-4" />
              </Button>
            )}
          </div>
          <p className="mt-1 text-center text-[11px] text-muted-foreground">
            O Mentor IA pode cometer erros. Verifique informações importantes.
          </p>
        </div>
      </div>
    </div>
  );
};

export default MentorIA;
